import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React from 'react';

import cs from 'app/components/Alerts/styles.module.scss';
import {AlertPolicy} from 'app/modules/Remote/Feature';
import {AlertNoteType, StateApiNote} from 'app/stores/NotesStore';
import colors from 'app/utils/colorUtils';

export interface AlertTypeConfiguration {
  /** Short name for the change type, for card tags and filters. */
  label: string;
  icon: B.IconName;
  color: string;
  /** Soft-tinted square behind the icon. */
  chipClassName: string;
}

/**
 * One description of each Lookout change type, shared by the policy rows, the
 * cards, the filter menu and the action bar.
 */
export const ALERT_TYPE_CONFIGURATIONS: Record<AlertNoteType, AlertTypeConfiguration> = {
  vegetationDrop: {
    label: 'Vegetation drop',
    icon: 'tree',
    color: colors.green,
    chipClassName: cs.chipVegetationDrop,
  },
  ownershipChange: {
    label: 'Parcel ownership',
    icon: 'key',
    color: colors.orange,
    chipClassName: cs.chipOwnershipChange,
  },
  parcelAreaChange: {
    label: 'Parcel area',
    icon: 'detection',
    color: colors.lightOrange,
    chipClassName: cs.chipParcelAreaChange,
  },
  customAlert: {
    label: 'Custom alert',
    icon: 'notifications',
    color: colors.primaryIntent,
    chipClassName: cs.chipCustomAlert,
  },
};

/**
 * Whether a note was written for this policy. The id lives in the note's JSONB
 * properties, so compare as strings rather than trusting it arrived as a number.
 */
export const noteMatchesAlertPolicy = (
  note: Pick<StateApiNote, 'properties'>,
  alertPolicy: Pick<AlertPolicy, 'id'>
): boolean => {
  const policyId = note.properties?.alert_policy_id;
  return policyId !== undefined && policyId !== null && String(policyId) === String(alertPolicy.id);
};

export const findAlertPolicyForNote = (
  note: Pick<StateApiNote, 'properties'>,
  alertPolicies: AlertPolicy[]
): AlertPolicy | undefined => alertPolicies.find((policy) => noteMatchesAlertPolicy(note, policy));

/**
 * What to call an alert of this type. Vegetation and custom alerts come from a
 * policy the user named, so that name wins over the generic type label.
 */
export const getAlertTypeLabel = (type: AlertNoteType, alertPolicy?: AlertPolicy): string =>
  (type === 'vegetationDrop' || type === 'customAlert') && alertPolicy?.name
    ? alertPolicy.name
    : ALERT_TYPE_CONFIGURATIONS[type].label;

/** Soft-tinted square holding a change type's icon. */
export const AlertTypeChip: React.FC<{type: AlertNoteType; className?: string}> = ({
  type,
  className,
}) => {
  const {icon, chipClassName} = ALERT_TYPE_CONFIGURATIONS[type];
  return (
    <span className={classnames(cs.typeChip, chipClassName, className)}>
      <B.Icon icon={icon} size={14} />
    </span>
  );
};
